import { useCallback, useEffect, useState } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { ErrorState, LoadingSpinner } from '../../components/common';
import { migrationService, type ScopeView } from '../../services/migration';
import { describeSelector, nextStage, previousStage, STAGE_HELP, STAGE_LABELS } from './logic';
import { ImportPanel } from './ImportPanel';
import { TunePanel } from './TunePanel';
import { ComparePanel } from './ComparePanel';
import { CutoverPanel } from './CutoverPanel';

const STAGES = ['connect', 'import', 'tune', 'silent', 'live'] as const;

/** One scope: where it is, how to move it, and the tools for its stage. */
export function ScopeDetail({ scopeId, onChanged, onDeleted }: { scopeId: string; onChanged: () => void; onDeleted: () => void }) {
  const { tenantId } = useTenant();
  const [scope, setScope] = useState<ScopeView | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      setScope(await migrationService.getScope(tenantId, scopeId));
      setError(null);
    } catch (err) {
      setError(err);
    }
  }, [tenantId, scopeId]);
  useEffect(() => {
    setScope(null);
    void load();
  }, [load]);

  const reloadAll = useCallback(async () => {
    await load();
    onChanged();
  }, [load, onChanged]);

  const move = async (to: (typeof STAGES)[number]) => {
    if (!tenantId || !scope) return;
    setBusy(true);
    setError(null);
    try {
      await migrationService.changeStage(tenantId, scope.id, to);
      await reloadAll();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!tenantId || !scope) return;
    if (!window.confirm(`Delete scope ${scope.name}?`)) return;
    setBusy(true);
    try {
      await migrationService.deleteScope(tenantId, scope.id);
      onDeleted();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  if (error !== null && !scope) return <ErrorState error={error} onRetry={() => void load()} />;
  if (!scope) return <LoadingSpinner />;

  const stage = scope.stage;
  const next = nextStage(stage);
  const prev = previousStage(stage);
  return (
    <section className="card" aria-label={`Scope ${scope.name}`}>
      <h2>{scope.name}</h2>
      <p className="text-muted">Covers: {describeSelector(scope.selector as Record<string, unknown>)}</p>
      <ol className="stage-editor" aria-label="Stages">
        {STAGES.map((s) => (
          <li key={s} aria-current={s === stage ? 'step' : undefined} className={s === stage ? 'status-badge status-active' : 'status-badge status-disabled'}>
            {STAGE_LABELS[s]}
          </li>
        ))}
      </ol>
      <p>{STAGE_HELP[stage]}</p>
      {error !== null && <ErrorState error={error} />}
      <div className="page-actions">
        {prev && (
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => void move(prev)}>
            Back to {STAGE_LABELS[prev]}
            {prev === 'connect' ? ' (releases the hold)' : ''}
          </button>
        )}
        {next && (
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void move(next)}>
            Move on to {STAGE_LABELS[next]}
          </button>
        )}
        {stage === 'connect' && (
          <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => void remove()}>
            Delete scope
          </button>
        )}
      </div>

      {(stage === 'import' || stage === 'tune' || stage === 'silent') && <ImportPanel scope={scope} />}
      {(stage === 'tune' || stage === 'silent') && <TunePanel scope={scope} />}
      {(stage === 'silent' || stage === 'tune' || stage === 'import') && <ComparePanel scope={scope} />}
      {(stage === 'silent' || stage === 'live') && <CutoverPanel scope={scope} onChanged={reloadAll} />}
    </section>
  );
}
